import { createSpinner, createTdWithElement, createTdWithText, createImg, createTableBodyLoadingIndicator } from "../DomUtilities/ElementsCreator";
import { insertBody } from "../DomUtilities/ElementsUtil";
import TableSort from "../Sorting/TableSort";
import ITableSort from "../Sorting/ITableSort";
import ITablePaginator from "../Pagination/ITablePaginator";
import TablePaginator from "../Pagination/TablePaginator";
import debouncer from "../Utilities/debouncer";
import CreateUserModal from "./CreateUserModal";
import UpdateUserModal from "./UpdateUserModal";
import { fetchUsersList } from "../HTTPRequests/UserRequests";
import User from "../Models/User";
import PageModel from "../Models/ViewModels/PageModel";
import openErrorModal from "../Error/ErrorModal";
import Mediator from "../Utilities/Mediator";
import UserInvitation from "./UserInvitation";
import { getStoresNames } from "../HTTPRequests/StoreRequests";

export default class Users implements ITableSort, ITablePaginator {
	table: HTMLTableElement;
	searchBar: HTMLInputElement;
	createBtn: HTMLButtonElement;
	searchValue: string;
	paginator: TablePaginator;
	sortHandler: TableSort;

	constructor() {
		this.table = document.querySelector('#users');
		this.searchBar = document.querySelector('input.search-bar');
		this.createBtn = document.querySelector('.create-btn');
		this.paginator = new TablePaginator(this);
		this.sortHandler = new TableSort(this);

		this.init();
	}

	private init(): void {
		Mediator.on("userCrudOperationFinished", () => this.fetchList());
		this.fetchList();
		this.addSearchInputListener();
		this.sortHandler.addSortingIconsClickListeners();
		this.paginator.addPaginationEventsListeners();
		new CreateUserModal(this.createBtn).addCreateModalClickListener();
	}

	public fetchList() {
		this.showLoadingIndicator();
		fetchUsersList(this.SearchValue, this.sortHandler.sortValue, this.sortHandler.sortAscending, this.paginator.currentPage, this.paginator.pageSize)
			.then((response: PageModel<User>) => {
				this.updateTable(response);
			}, () => { //backend error
				openErrorModal();
			}).catch(() => { //frontend error
				openErrorModal();
			});
	}

	private showLoadingIndicator() {
		insertBody(this.table, createTableBodyLoadingIndicator());
	}

	private updateTable(data: PageModel<User>) {
		this.paginator.currentPage = data.pageNumber;
		this.paginator.total = data.total;
		this.paginator.pageSize = data.pageSize;
		this.renderTable(data.list);
		new UpdateUserModal(this.createBtn).addUpdateModalsClickListeners();
	}

	private renderTable(list: Array<User>) {
		insertBody(this.table,
			this.renderBody(list)
		);
		this.paginator.renderFoot();
	}

	private renderBody(usersList: Array<User>) {
		const tbody = document.createElement('tbody');
		const headers = this.table.querySelectorAll('th');

		if (usersList.length) {
			const storeIds = new Set<string>();
			const storeTds: HTMLTableCellElement[] = [];

			for (let i = 0; i < usersList.length; i++) {
				const tr = document.createElement('tr');
				const imgEdit = createImg('/assets/edit.svg', 'edit icon');

				const tdName = createTdWithText(usersList[i].name);
				const tdPhone = createTdWithText(usersList[i].phoneNumber);
				const tdRole = createTdWithText(usersList[i].roles[0]);
				const tdEmail = createTdWithText(usersList[i].email);

				tr.classList.add('table__row');
				tr.id = usersList[i].id;

				tr.append(tdName);
				tr.append(tdPhone);
				tr.append(tdRole);
				tr.append(tdEmail);

				if (headers.length === 6) {
					const tdStore = document.createElement('td');
					if (usersList[i].storeId) {
						tdStore.dataset.storeId = usersList[i].storeId;
						tdStore.append(createSpinner());
						storeTds.push(tdStore);
						storeIds.add(usersList[i].storeId);
					}
					tr.append(tdStore);
				}

				let lastTd: HTMLTableCellElement;
				if (usersList[i].hasPassword) {
					lastTd = createTdWithElement(imgEdit);
				} else {
					const invitationIcon = document.createElement('i');
					invitationIcon.title = this.table.dataset.invitaion;
					invitationIcon.classList.add("icon-share-alt");
					lastTd = createTdWithElement(invitationIcon, imgEdit);
					new UserInvitation(invitationIcon, usersList[i].id, usersList[i].name);
				}

				tr.append(lastTd);
				tbody.append(tr);
			}

			if (headers.length === 6 && storeIds.size) {
				this.showStoreNames(storeIds, storeTds);
			}
		} else {
			const emptyText = this.table.dataset.empty;
			const tr = document.createElement('tr');
			const td = createTdWithText(emptyText);
			td.colSpan = headers.length + 1;
			td.style.textAlign = 'center';
			tr.append(td);
			tbody.append(tr);
		}

		return tbody;
	}

	private get SearchValue(): string {
		return this.searchBar.value.trim();
	}

	private addSearchInputListener() {
		const debounce = debouncer(this.searchList.bind(this), 400, false);

		this.searchBar.oninput = (event) => {
			if ((event.target["value"]).trim() != this.searchValue) {
				debounce(event);
			}
		}
	}

	private searchList() {
		this.searchValue = this.SearchValue;
		this.fetchList();
	}

	private showStoreNames(storeIds: Set<string>, storeTds: HTMLTableCellElement[]) {
		getStoresNames([...storeIds])
			.then(result => {
				for (const storeTd of storeTds) {
					storeTd.innerHTML = "";
					storeTd.innerText = result.get(storeTd.dataset.storeId) || "";
				}
			}, () => { // backend error
				storeTds.forEach(td => td.innerHTML = "");
			})
			.catch(() => { // frontend error
				storeTds.forEach(td => td.innerHTML = "");
			})
	}
}
